import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NavLink } from "./NavLink";
import { Menu, Search, X } from "lucide-react";
import logo from "@/assets/logo-optimized.webp";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogoClick = () => {
    navigate("/");
    setTimeout(() => {
      const heroElement = document.getElementById("hero");
      if (heroElement) {
        heroElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <button onClick={handleLogoClick} className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
            <img 
              src={logo} 
              alt="Oh Mind Guide" 
              className="h-12 w-auto" 
              width={48} 
              height={48} 
              fetchPriority="high"
              decoding="async"
            />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5">
            {/* Main pillars first */}
            <NavLink
              to="/articles"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {t.nav.articles}
            </NavLink>
            <NavLink
              to="/games"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {t.nav.games}
            </NavLink>
            <NavLink
              to="/exercises"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {t.nav.exercises}
            </NavLink>
            <NavLink
              to="/questionnaires"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {t.nav.questionnaires}
            </NavLink>
            
            {/* Special features */}
            <NavLink
              to="/mind-buddy"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm flex items-center gap-1"
              activeClassName="text-primary"
            >
              <span>💛</span>
              Omi
            </NavLink>

            {/* Info links */}
            <NavLink
              to="/what-is"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {language === "ru" ? "Что это" : language === "de" ? "Was ist Oh Mind Guide" : "What is Oh Mind Guide"}
            </NavLink>
            <NavLink
              to="/about"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {language === "ru" ? "Обо мне" : language === "de" ? "Über mich" : "About"}
            </NavLink>
            <NavLink
              to="/faq"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              FAQ
            </NavLink>
            <NavLink
              to="/saved"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
            >
              {language === "ru" ? "Сохранённое" : language === "de" ? "Gespeichert" : "Saved"}
            </NavLink>
            <NavLink
              to="/search"
              className="text-foreground hover:text-primary transition-colors font-medium text-sm"
              activeClassName="text-primary"
              aria-label="Search"
            >
              <Search size={18} aria-hidden="true" />
            </NavLink>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-1 text-sm">
              {(["en", "de", "ru"] as const).map((lang, i) => (
                <span key={lang} className="flex items-center">
                  {i > 0 && <span className="text-muted-foreground mx-0.5">|</span>}
                  <button
                    onClick={() => setLanguage(lang)}
                    className={`px-1.5 py-1 rounded transition-colors ${
                      language === lang
                        ? "font-semibold text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                </span>
              ))}
            </div>
            
            <a
              href="mailto:margarita@ohmindguide.com"
              className="bg-primary text-primary-foreground px-6 py-2 rounded-full hover:shadow-hover transition-all duration-300 font-medium"
            >
              {t.nav.contact}
            </a>
          </div>

          {/* Mobile Main Links - Always visible */}
          <div className="md:hidden flex items-center gap-1 flex-nowrap">
            <NavLink
              to="/articles"
              className="text-foreground hover:text-primary transition-colors font-medium text-[9px] px-0.5 py-1 whitespace-nowrap leading-none"
              activeClassName="text-primary"
            >
              {t.nav.articles}
            </NavLink>
            <NavLink
              to="/games"
              className="text-foreground hover:text-primary transition-colors font-medium text-[9px] px-0.5 py-1 whitespace-nowrap leading-none"
              activeClassName="text-primary"
            >
              {t.nav.games}
            </NavLink>
            <NavLink
              to="/exercises"
              className="text-foreground hover:text-primary transition-colors font-medium text-[9px] px-0.5 py-1 whitespace-nowrap leading-none"
              activeClassName="text-primary"
            >
              {t.nav.exercises}
            </NavLink>
            <NavLink
              to="/questionnaires"
              className="text-foreground hover:text-primary transition-colors font-medium text-[9px] px-0.5 py-1 whitespace-nowrap leading-none"
              activeClassName="text-primary"
            >
              {t.nav.questionnaires}
            </NavLink>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 text-foreground ml-1"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 flex flex-col gap-4 bg-background/95 backdrop-blur-md rounded-xl p-4 -mx-2 shadow-lg border border-border/50">
            <NavLink
              to="/articles"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.nav.articles}
            </NavLink>
            <NavLink
              to="/games"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.nav.games}
            </NavLink>
            <NavLink
              to="/exercises"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.nav.exercises}
            </NavLink>
            <NavLink
              to="/questionnaires"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.nav.questionnaires}
            </NavLink>
            
            <div className="border-t border-border/50 my-2" />
            
            <NavLink
              to="/mind-buddy"
              className="text-foreground hover:text-primary transition-colors font-medium text-left flex items-center gap-2"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>💛</span>
              Omi
            </NavLink>

            <div className="border-t border-border/50 my-2" />
            
            <NavLink
              to="/what-is"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {language === "ru" ? "Что это" : language === "de" ? "Was ist Oh Mind Guide" : "What is Oh Mind Guide"}
            </NavLink>
            <NavLink
              to="/about"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {language === "ru" ? "Обо мне" : language === "de" ? "Über mich" : "About"}
            </NavLink>
            <NavLink
              to="/faq"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              FAQ
            </NavLink>
            <NavLink
              to="/saved"
              className="text-foreground hover:text-primary transition-colors font-medium text-left"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {language === "ru" ? "Сохранённое" : language === "de" ? "Gespeichert" : "Saved"}
            </NavLink>
            <NavLink
              to="/search"
              className="text-foreground hover:text-primary transition-colors font-medium text-left flex items-center gap-2"
              activeClassName="text-primary"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Search size={18} aria-hidden="true" />
              Search
            </NavLink>
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center gap-2 text-sm py-2">
              {(["en", "de", "ru"] as const).map((lang, i) => (
                <span key={lang} className="flex items-center">
                  {i > 0 && <span className="text-muted-foreground">|</span>}
                  <button
                    onClick={() => setLanguage(lang)}
                    className={`px-3 py-1 rounded transition-colors ${
                      language === lang
                        ? "font-semibold text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                </span>
              ))}
            </div>
            
            <a
              href="mailto:margarita@ohmindguide.com"
              className="bg-primary text-primary-foreground px-6 py-2 rounded-full hover:shadow-hover transition-all duration-300 font-medium text-center"
            >
              {t.nav.contact}
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
